import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { byValue, ascending, occurrence, descending } from '@Containers/ResultsFilters/ResultsFiltersConstants';
import Catalyst from '@Services/Catalyst';

const trackSorting = (context, filter, direction, type) => {
  const title = filter.get('title').toLowerCase();
  const directionForAA = direction === ascending ? 'up' : 'down';
  const typeForAA = type === occurrence ? 'occurrences' : 'alphanumerical';
  Catalyst.trackEvent('productFeatureUsed', {
    feature: {
      name: `rx2:results:${context}:facet:${title}:sort by ${typeForAA}:${directionForAA}`, // `rx2:results:substances:facet:commercial suppliers:sort by alphanumerical:up`,
    },
  });
};

const barChartCheckboxesDialogHandlers = (Component) => {
  function BarChartCheckboxesDialogHandlersHOC(props) {
    const {
      id,
      setChunksSortingType,
      setChunksSorting,
      context,
      filter,
      sortingType,
      filterId,
      keyifyParamsForFiltersRequest,
      updateGroupByIdInChunksOfFilter,
      setActiveChunkId,
      limitTo,
      exclude,
      selfClose,
      sorting,
      deselectAllChunksOfFilter,
    } = props;

    const onByValue = useCallback(() => {
      setChunksSortingType(id, byValue);
      trackSorting(context, filter, ascending, byValue);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, context, filter]);

    const onOccurrence = useCallback(() => {
      setChunksSortingType(id, occurrence);
      trackSorting(context, filter, descending, occurrence);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, context, filter]);

    const onAscending = useCallback(() => {
      setChunksSorting(id, ascending);
      trackSorting(context, filter, ascending, sortingType);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, context, filter, sortingType]);

    const onDescending = useCallback(() => {
      setChunksSorting(id, descending);
      trackSorting(context, filter, descending, sortingType);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, context, filter, sortingType]);

    const onCheckboxClick = useCallback(
      (groupId, selected) => {
        updateGroupByIdInChunksOfFilter(keyifyParamsForFiltersRequest, filterId, groupId, {
          selected: !selected,
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [keyifyParamsForFiltersRequest, filterId]
    );

    const onClickGoToButton = useCallback(
      (goToInputValue) => {
        const index = parseInt(goToInputValue, 10) - 1;
        setActiveChunkId(id, index);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [id]
    );

    const onClickLimitTo = useCallback(() => {
      selfClose();
      // Just give it a bit more time to close the dialog
      setTimeout(() => {
        limitTo(filter, sortingType, sorting);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sortingType, sorting]);

    const onClickExclude = useCallback(() => {
      selfClose();
      // Just give it a bit more time to close the dialog
      setTimeout(() => {
        exclude(filter, sortingType, sorting);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sortingType, sorting]);

    const onClickClearSelected = useCallback(() => {
      deselectAllChunksOfFilter(keyifyParamsForFiltersRequest, filterId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyifyParamsForFiltersRequest, filterId]);

    return (
      <Component
        {...props}
        onByValue={onByValue}
        onOccurrence={onOccurrence}
        onAscending={onAscending}
        onDescending={onDescending}
        onCheckboxClick={onCheckboxClick}
        onClickGoToButton={onClickGoToButton}
        onClickLimitTo={onClickLimitTo}
        onClickExclude={onClickExclude}
        onClickClearSelected={onClickClearSelected}
      />
    );
  }

  BarChartCheckboxesDialogHandlersHOC.propTypes = {
    id: PropTypes.string,
    setChunksSortingType: PropTypes.func,
    setChunksSorting: PropTypes.func,
    context: PropTypes.string,
    filter: PropTypes.instanceOf(Map),
    sortingType: PropTypes.oneOf([byValue, occurrence]),
    filterId: PropTypes.string,
    keyifyParamsForFiltersRequest: PropTypes.string,
    updateGroupByIdInChunksOfFilter: PropTypes.func,
    setActiveChunkId: PropTypes.func,
    limitTo: PropTypes.func,
    exclude: PropTypes.func,
    selfClose: PropTypes.func,
    sorting: PropTypes.oneOf([ascending, descending]),
    deselectAllChunksOfFilter: PropTypes.func,
  };

  return BarChartCheckboxesDialogHandlersHOC;
};

export default barChartCheckboxesDialogHandlers;
